import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import CourseCard from "./CourseCard";
import Button from "./Button";
import { useLanguage } from "../../context/LanguageContext";

const CourseCarousel = ({ courses }) => {
    const { language } = useLanguage();
    const carouselRef = useRef(null);
    const cardRefs = useRef([]); // Store individual course card refs
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollAmount, setScrollAmount] = useState(0);

    useEffect(() => {
        if (cardRefs.current[selectedIndex]) {
            setScrollAmount(cardRefs.current[selectedIndex].offsetWidth + 24); // Account for spacing
        }
    }, [courses, selectedIndex]);

    const scrollToIndex = (index) => {
        if (carouselRef.current && cardRefs.current[index]) {
            const scrollLeft =
                cardRefs.current[index].offsetLeft -
                (carouselRef.current.offsetWidth - cardRefs.current[index].offsetWidth) / 2;
            carouselRef.current.scrollTo({ left: scrollLeft, behavior: "smooth" });
            setSelectedIndex(index);
        }
    };

    const nextSlide = () => {
        if (selectedIndex < courses.length - 1) {
            scrollToIndex(selectedIndex + 1);
        }
    };

    const prevSlide = () => {
        if (selectedIndex > 0) {
            scrollToIndex(selectedIndex - 1);
        }
    };

    return (
        <div className="relative w-full group py-6">
            {/* Carousel Container */}
            <div className="relative overflow-hidden rounded-xl shadow-lg border border-gray-300 p-0 md:p-6">
                <div
                    ref={carouselRef}
                    className="flex space-x-6 overflow-x-auto no-scrollbar snap-x scroll-smooth px-1 md:px-6 py-4"
                >
                    {courses.map((course, index) => (
                        <motion.div
                            key={course.id}
                            ref={(el) => (cardRefs.current[index] = el)}
                            className={`snap-center flex-shrink-0 w-full sm:w-fit px-2 md:px-10 rounded-lg mx-3 transition-all ${
                                selectedIndex === index ? "scale-105 bg-yellow-100 shadow-md" : ""
                            }`}
                            whileHover={{ scale: 1.02 }}
                        >
                            <CourseCard {...course} />
                        </motion.div>
                    ))}
                </div>
            </div>

            <Button
                onClick={prevSlide}
                disabled={selectedIndex <= 0}
                className={`absolute z-20 -right-4 md:right-1 border-none top-1/2 transform -translate-y-1/2 p-3 rounded-full shadow-xl transition-all duration-300 ${
                    selectedIndex <= 0
                        ? "opacity-40 cursor-not-allowed bg-gray-300"
                        : "bg-germanRed-500 hover:bg-germanRed-600 text-white"
                }`}
            >
                <Icon icon="ic:round-chevron-right" className="text-2xl" />
            </Button>

            <Button
                onClick={nextSlide}
                disabled={selectedIndex >= courses.length - 1}
                className={`absolute border-none z-20 -left-4 md:left-1 top-1/2 transform -translate-y-1/2 p-3 rounded-full shadow-xl transition-all duration-300 ${
                    selectedIndex >= courses.length - 1
                        ? "opacity-40 cursor-not-allowed bg-gray-300"
                        : "bg-germanRed-500 hover:bg-germanRed-600 text-white"
                }`}
            >
                <Icon icon="ic:round-chevron-left" className="text-2xl" />
            </Button>

            <div className="flex justify-center mt-4 gap-4">
                {courses.map((_, index) => (
                    <motion.div
                        key={index}
                        className={`w-3 h-3 rounded-full transition-all ${
                            selectedIndex === index ? "bg-yellow-500 scale-125" : "bg-gray-300"
                        }`}
                        onClick={() => scrollToIndex(index)}
                        whileHover={{ scale: 1.2 }}
                    />
                ))}
            </div>
        </div>
    );
};

export default CourseCarousel;
